import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Edit, Trash2, User, Building, Phone, Mail, Calendar } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const WardenSetting = () => {
  const [wardens, setWardens] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWarden, setEditingWarden] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    staff_id: '',
    staff_name: '',
    hostel_id: '',
    hostel_name: '',
    designation: 'Warden',
    phone: '',
    email: '',
    joining_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchWardens();
    fetchHostels();
    fetchStaff();
  }, []);

  const fetchWardens = async () => {
    try {
      const response = await apiClient.get('/api/hostel/wardens');
      setWardens(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching wardens:', error);
      setWardens([]);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await apiClient.get('/api/hostel/hostels');
      setHostels(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching hostels:', error);
      setHostels([]);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await apiClient.get('/api/staff/');
      setStaffList(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching staff:', error);
      setStaffList([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingWarden) {
        await apiClient.put(`/api/hostel/wardens/${editingWarden.id}`, formData);
      } else {
        await apiClient.post('/api/hostel/wardens', formData);
      }
      alert('Warden saved successfully!');
      fetchWardens();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving warden:', error);
      alert('Failed to save warden');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this warden?')) {
      try {
        await apiClient.delete(`/api/hostel/wardens/${id}`);
        fetchWardens();
      } catch (error) {
        console.error('Error deleting warden:', error);
        alert('Failed to delete warden');
      }
    }
  };

  const handleEdit = (warden) => {
    setEditingWarden(warden);
    setFormData({
      staff_id: warden.staff_id || '',
      staff_name: warden.staff_name || '',
      hostel_id: warden.hostel_id || '',
      hostel_name: warden.hostel_name || '',
      designation: warden.designation || 'Warden',
      phone: warden.phone || '',
      email: warden.email || '',
      joining_date: warden.joining_date || new Date().toISOString().split('T')[0]
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingWarden(null);
    setFormData({
      staff_id: '',
      staff_name: '',
      hostel_id: '',
      hostel_name: '',
      designation: 'Warden',
      phone: '',
      email: '',
      joining_date: new Date().toISOString().split('T')[0]
    });
  };

  const handleHostelChange = (hostelId) => {
    const hostel = hostels.find(h => h.id === hostelId);
    if (hostel) {
      setFormData({ ...formData, hostel_id: hostelId, hostel_name: hostel.hostel_name });
    }
  };

  const handleStaffChange = (staffId) => {
    const staff = staffList.find(s => s.id === staffId);
    if (staff) {
      setFormData({
        ...formData,
        staff_id: staff.employee_id || staff.id,
        staff_name: staff.full_name || staff.name || '',
        phone: staff.phone || staff.mobile || formData.phone,
        email: staff.email || formData.email
      });
    }
  };

  // Get available hostels (not already assigned to a warden)
  const getAvailableHostels = () => {
    const assignedHostelIds = wardens
      .filter(w => !editingWarden || w.id !== editingWarden.id)
      .map(w => w.hostel_id);
    return hostels.filter(h => !assignedHostelIds.includes(h.id));
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Warden Management
          </CardTitle>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Assign Warden
          </Button>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="p-4 bg-blue-50 border-blue-200">
              <p className="text-sm text-blue-600">Total Wardens</p>
              <p className="text-2xl font-bold text-blue-700">{wardens.length}</p>
            </Card>
            <Card className="p-4 bg-green-50 border-green-200">
              <p className="text-sm text-green-600">Hostels with Warden</p>
              <p className="text-2xl font-bold text-green-700">{wardens.length}</p>
            </Card>
            <Card className="p-4 bg-yellow-50 border-yellow-200">
              <p className="text-sm text-yellow-600">Hostels without Warden</p>
              <p className="text-2xl font-bold text-yellow-700">{hostels.length - wardens.length}</p>
            </Card>
          </div>

          {/* Wardens Table */}
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Staff Name</th>
                <th className="text-left p-3">Employee ID</th>
                <th className="text-left p-3">Hostel</th>
                <th className="text-left p-3">Phone</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Joining Date</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {wardens.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center p-8 text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No wardens assigned yet.</p>
                    <p className="text-sm">Click "Assign Warden" to add a warden to a hostel.</p>
                  </td>
                </tr>
              ) : (
                wardens.map((warden) => (
                  <tr key={warden.id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{warden.staff_name}</p>
                          <p className="text-xs text-gray-500">{warden.designation || 'Warden'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-1 text-xs rounded bg-gray-100">{warden.staff_id}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Building className="w-4 h-4 text-gray-400" />
                        {warden.hostel_name}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Phone className="w-3 h-3 text-gray-400" />
                        {warden.phone || '-'}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3 text-gray-400" />
                        {warden.email || '-'}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        {warden.joining_date ? formatDate(warden.joining_date) : '-'}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2 justify-center">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(warden)}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(warden.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Add/Edit Warden Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingWarden ? 'Edit' : 'Assign'} Warden</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Staff Selection */}
            <div>
              <Label>Select Staff *</Label>
              <Select 
                value={staffList.find(s => s.employee_id === formData.staff_id || s.id === formData.staff_id)?.id || ''} 
                onValueChange={handleStaffChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="-- Select Staff Member --" />
                </SelectTrigger>
                <SelectContent>
                  {staffList.length === 0 ? (
                    <SelectItem value="no-staff" disabled>No staff available</SelectItem>
                  ) : (
                    staffList.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        {staff.full_name || staff.name} ({staff.employee_id}) - {staff.designation || 'Staff'}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Select a staff member to assign as warden</p>
            </div>

            {/* Staff Info Display */}
            {formData.staff_name && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p><strong>Selected:</strong> {formData.staff_name}</p>
                <p><strong>Employee ID:</strong> {formData.staff_id}</p>
              </div>
            )}

            {/* Hostel Selection */}
            <div>
              <Label>Assign to Hostel *</Label>
              <Select value={formData.hostel_id} onValueChange={handleHostelChange}>
                <SelectTrigger>
                  <SelectValue placeholder="-- Select Hostel --" />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableHostels().length === 0 ? (
                    <SelectItem value="no-hostel" disabled>All hostels have wardens assigned</SelectItem>
                  ) : (
                    getAvailableHostels().map((hostel) => (
                      <SelectItem key={hostel.id} value={hostel.id}>
                        {hostel.hostel_name} ({hostel.hostel_type})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Designation */}
            <div>
              <Label>Designation</Label>
              <Select value={formData.designation} onValueChange={(v) => setFormData({ ...formData, designation: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Warden">Warden</SelectItem>
                  <SelectItem value="Chief Warden">Chief Warden</SelectItem>
                  <SelectItem value="Assistant Warden">Assistant Warden</SelectItem>
                  <SelectItem value="Hostel Superintendent">Hostel Superintendent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input 
                  value={formData.phone} 
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={formData.email} 
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Email address"
                />
              </div>
            </div>

            {/* Joining Date */}
            <div>
              <Label>Warden Joining Date *</Label>
              <Input 
                type="date" 
                required 
                value={formData.joining_date} 
                onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Date when staff was assigned as warden</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
              <Button type="submit" disabled={loading || !formData.staff_id || !formData.hostel_id}>
                {loading ? 'Saving...' : 'Save Warden'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WardenSetting;
